import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";
import type { Phase } from "../wizard-data";

const K = keys.quickSetup;

/**
 * Desktop navy sidebar: Trioty wordmark + headline/sub + 5-step vertical
 * stepper (done = gold ✓ disc, current = gold ring + number, upcoming = 25%
 * white) + diamond motif + store chip. `topLevelIndex` is the index into
 * `phases` (the 5 top-level phases from `useWizardData()`), 0-based.
 */
export function SetupSidebar({
  topLevelIndex,
  done,
  phases,
  storeName,
  logo,
}: {
  topLevelIndex: number;
  done: boolean;
  phases: Phase[];
  storeName: string;
  logo?: string | null;
}): ReactElement {
  const t = useTranslations();
  const curIdx = done ? phases.length : topLevelIndex;

  return (
    <aside
      style={{
        width: 380,
        flexShrink: 0,
        background: "var(--navy)",
        color: "var(--on-navy)",
        display: "flex",
        flexDirection: "column",
        padding: "44px 40px",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        className="qs-diamond"
        style={{ top: -160, insetInlineEnd: -130 }}
      />

      <div style={{ position: "relative" }}>
        {/* The real app logo (same asset the main dark sidebar uses), not a
            hand-drawn mark. Stays Latin in both locales (brand decision). */}
        <img
          src="/logo-sidebar-dark.svg"
          alt={t(K.brand.name)}
          style={{ height: 30, width: "auto", display: "block" }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 40, position: "relative" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: -0.4,
          }}
        >
          {t(K.brand.headlineLine1)} {t(K.brand.headlineLine2)}
        </h1>
        <p style={{ margin: 0, fontSize: 14, color: "var(--on-navy-muted)", lineHeight: 1.55 }}>
          {t(K.brand.subtitle)}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 40, position: "relative" }}>
        {phases.map((ph, i) => {
          const isDone = curIdx > i;
          const isCurrent = curIdx === i;
          return (
            <div key={ph.id}>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    flexShrink: 0,
                    fontSize: 13,
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    background: isDone ? "var(--accent)" : "transparent",
                    color: isDone ? "var(--navy)" : isCurrent ? "var(--accent)" : "rgba(251,249,245,0.45)",
                    border: isDone
                      ? "none"
                      : isCurrent
                        ? "2px solid var(--accent)"
                        : "2px solid rgba(251,249,245,0.25)",
                  }}
                >
                  {isDone ? ic(Icons.check, 14, { stroke: 3 }) : i + 1}
                </span>
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: isCurrent || isDone ? 700 : 600,
                    color: isCurrent
                      ? "#fff"
                      : isDone
                        ? "rgba(251,249,245,0.75)"
                        : "rgba(251,249,245,0.45)",
                  }}
                >
                  {ph.name}
                </span>
              </div>
              {i < phases.length - 1 && (
                <span
                  style={{
                    display: "block",
                    width: 2,
                    height: 22,
                    marginInlineStart: 14,
                    background: isDone ? "var(--accent)" : "rgba(251,249,245,0.15)",
                    opacity: isDone ? 0.5 : 1,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
          borderTop: "1px solid rgba(251,249,245,0.14)",
          paddingTop: 20,
          position: "relative",
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: logo ? 8 : 999,
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: logo ? "#fff" : "var(--accent)",
            color: "var(--navy)",
            fontWeight: 800,
            fontSize: 14,
          }}
        >
          {logo ? (
            <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            storeName.charAt(0).toUpperCase()
          )}
        </span>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{storeName}</span>
          <span style={{ fontSize: 12, color: "var(--on-navy-faint)" }}>{t(K.brand.settingUpAccount)}</span>
        </div>
      </div>
    </aside>
  );
}
